// for dashboard related operations

import type { RoleAction, RoleCondition } from '../RoleRecommendationsService';

import { API_BASE } from '../../utils/constants';
import HttpServiceSingleton from '../HttpService/HttpService';

import { ROLES_PATH } from './constants';

/**
 * Interface for role list request filters
 */
export interface RoleFilters {
  status?: number[];
  type?: number[];
  needUpdate?: boolean[];
  name?: string[];
  description?: string[];
  duplicateOf?: string;
  entitlementValueName?: string[];
  endpointName?: string[];
  entitlementType?: string[];
  userMatchCount?: number[];
  yearlyRequestVolume?: number[];
  confidence?: number[];
  impact?: string[];
  objectName?: string[];
  objectValue?: string[];
  search?: string; // Search remains a single string
}

export interface RolePagination {
  max?: number;
  page?: number;
}

/**
 * Interface for role list request body
 */
export interface RoleListRequestBody {
  filters?: RoleFilters;
  pagination?: RolePagination;
}

/**
 * Interface for a single role (extending the existing structure with new fields)
 */
export interface Role {
  recommendedObjectKey: number;
  name: string;
  description: string;
  userMatchCount: number;
  includedUsers: number;
  impact: string;
  yearlyRequestVolume: number;
  entitlementsCount: number;
  confidence: number;
  conditions: RoleCondition[];
  actions: RoleAction[];
  entitlements?: RoleAction[]; // Backend changed actions to entitlements (transformed to 'actions' in service layer)
  isNew: boolean;
}

/**
 * Interface for role list response
 */
export interface RoleListResponse {
  data: Role[];
  pagination: {
    nextPage: number | null;
    currentPage: number;
  };
  userAccess?: 'edit' | 'view' | 'none';
  totalNewObjects?: number;
  totalCount?: number; // Total count of roles
}

export interface ActivateRoleRequestBody {
  activatedBy: number;
  comments?: string;
  name: string;
  description: string;
  entitlements: {
    entitlementValueName: string;
    entitlementTypeName: string;
    endpointName: string;
  }[];
  requestable?: boolean;
}

export interface ActivateRoleResponse {
  success: boolean;
  message: string;
  recommendedObjectKey: number;
  activatedBy: number;
  username: string | null;
  comments: string | null;
  roleId: number;
  data: {
    success: boolean;
    message: string;
    roleId: number;
    username: string | null;
    roleServiceUrl: string;
    roleCreationResponse: {
      msg: string;
    };
    removedEntitlements: unknown[];
  };
}

export interface RejectRoleRequestBody {
  rejectedBy: number;
  reason?: string;
}

export interface RejectRoleResponse {
  success: boolean;
  message: string;
  recommendedObjectKey: number;
  rejectedBy: number;
  comments: string;
  data: {
    success: boolean;
    message: string;
  };
}

/**
 * Service for Roles API operations
 */
const RolesService = {
  /**
   * Fetches roles list with filters and pagination
   * @param requestBody - Request body with filters and pagination
   * @returns Promise with roles list response
   */
  getRolesList(
    requestBody: RoleListRequestBody = {}
  ): Promise<RoleListResponse> {
    const url = `${API_BASE}${ROLES_PATH}/`;

    return HttpServiceSingleton.post(url, requestBody, undefined, true, {
      shouldThrowOnError: true,
    }).then(
      (
        response: RoleListResponse & {
          data: (Role & { entitlements: RoleAction[] })[];
        }
      ) => ({
        ...response,
        data: response.data.map((role) => ({
          ...role,
          // backend sends 'entitlements', map to 'actions' so the app doesn't break
          actions: role.entitlements,
        })),
      })
    ) as Promise<RoleListResponse>;
  },

  /**
   * Fetches detailed information for a specific role
   * @param recommendedObjectKey - Unique identifier for the role
   * @returns Promise with role details
   */
  getRoleDetails(recommendedObjectKey: number): Promise<Role> {
    const url = `${API_BASE}${ROLES_PATH}/${recommendedObjectKey}`;

    return HttpServiceSingleton.get(url) as Promise<Role>;
  },

  /**
   * Activates a role with the provided configuration
   * @param recommendedObjectKey - Unique identifier for the role
   * @param requestBody - Request body with activation details
   * @returns Promise with activation response
   */
  activateRole(
    recommendedObjectKey: string,
    requestBody: ActivateRoleRequestBody
  ): Promise<ActivateRoleResponse> {
    const url = `${API_BASE}${ROLES_PATH}/${recommendedObjectKey}/activate`;

    return HttpServiceSingleton.post(
      url,
      requestBody,
      undefined, // errorCallback
      true, // shouldStringifyBody
      { shouldThrowOnError: true } // for error handling
    ) as Promise<ActivateRoleResponse>;
  },

  /**
   * Rejects a role with the provided reason
   * @param recommendedObjectKey - Unique identifier for the role
   * @param requestBody - Request body with rejection details
   * @returns Promise with rejection response
   */
  rejectRole(
    recommendedObjectKey: number,
    requestBody: RejectRoleRequestBody
  ): Promise<RejectRoleResponse> {
    const url = `${API_BASE}${ROLES_PATH}/${recommendedObjectKey}/reject`;

    return HttpServiceSingleton.post(
      url,
      requestBody,
      undefined, // errorCallback
      true, // shouldStringifyBody
      { shouldThrowOnError: true } // for error handling
    ) as Promise<RejectRoleResponse>;
  },
};

export default RolesService;
